package fyi.moko.hotpl.domain.usecase

import fyi.moko.hotpl.domain.model.Friendship
import fyi.moko.hotpl.domain.model.InviteCode
import fyi.moko.hotpl.domain.model.InviteCodeGenerator
import fyi.moko.hotpl.domain.model.RandomInviteCodeGenerator
import fyi.moko.hotpl.domain.service.AuthService
import fyi.moko.hotpl.domain.service.SocialGraphService
import kotlin.time.Clock

/**
 * Returns the caller's invite code, creating one when none exists. With
 * [regenerate] the existing code is revoked first, invalidating anything
 * already shared.
 *
 * The display name captured on the code is what friends will see forever
 * (names are frozen at friendship creation), hence the fallback chain
 * displayName → linkedEmail → "Anonymous".
 */
class CreateInviteCodeUseCase(
    private val authService: AuthService,
    private val socialGraphService: SocialGraphService,
    private val codeGenerator: InviteCodeGenerator = RandomInviteCodeGenerator,
    private val clock: Clock = Clock.System,
) {
    suspend operator fun invoke(regenerate: Boolean = false): InviteCode {
        val uid = authService.ensureSignedIn()
        val existing = socialGraphService.findMyInviteCode(uid)
        if (existing != null) {
            if (!regenerate) return existing
            socialGraphService.deleteInviteCode(existing.code)
        }

        val ownerName = authService.displayName()
            ?: authService.linkedEmail()
            ?: Friendship.ANONYMOUS_NAME

        // The exists() probe dodges collisions cheaply; the rules' create-only policy on
        // /invite_codes is the backstop for the probe-vs-create race (a set() on an existing
        // doc is an update and is denied), which surfaces here as createInviteCode throwing
        // on the final attempt.
        var lastError: Exception? = null
        repeat(MAX_ATTEMPTS) {
            val invite = InviteCode(
                code = codeGenerator.newCode(),
                ownerUid = uid,
                ownerName = ownerName,
                createdAt = clock.now().toEpochMilliseconds(),
            )
            if (socialGraphService.inviteCodeExists(invite.code)) return@repeat
            try {
                socialGraphService.createInviteCode(invite)
                return invite
            } catch (e: kotlinx.coroutines.CancellationException) {
                throw e
            } catch (e: Exception) {
                lastError = e
            }
        }
        throw lastError ?: IllegalStateException("Could not mint a unique invite code")
    }

    companion object {
        const val MAX_ATTEMPTS = 3
    }
}
